// src/pages/Presentation/SmartVideoPlayerGallery.tsx
import React, {
  useRef,
  useEffect,
  useState,
  memo,
  CSSProperties,
  useMemo,
} from 'react';
import { useTranslation } from "react-i18next";

interface SmartVideoPlayerGalleryProps {
  videoUrl: string;
  poster?: string; 
  style?: CSSProperties;
  muted?: boolean;
  /** Prefer false in feed, true in overlay. */
  showControls?: boolean;
  /** Enable click-to-toggle when native controls are hidden. */
  clickToToggle?: boolean;
  onMutedChange?: (muted: boolean) => void; // NEW
  preload?: 'none' | 'metadata' | 'auto';
  musicId?: string | null;
  musicTitle?: string | null;
  musicArtist?: string | null;
}

function inferMime(url: string): string | undefined {
  const q = url.split('?')[0].toLowerCase();
  if (q.endsWith('.mp4')) return 'video/mp4';
  if (q.endsWith('.webm')) return 'video/webm';
  if (q.endsWith('.mov')) return 'video/quicktime';
  if (q.endsWith('.m4v')) return 'video/x-m4v';
  return undefined;
}

const SmartVideoPlayerGallery: React.FC<SmartVideoPlayerGalleryProps> = ({
  videoUrl,
  poster,
  style,
  muted = true,
  showControls = true,   // overlay-friendly default
  clickToToggle = true,  // feed-friendly default
  onMutedChange,
  preload = 'metadata',
  musicId,
  musicTitle,
  musicArtist,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wasMutedRef = useRef<boolean>(!!muted); // NEW

  const [isVisible, setIsVisible] = useState(false);
const [userPaused, setUserPaused] = useState(false);

  // Hover/tap-driven controls visibility
  const [isHover, setIsHover] = useState(false);
  const hoverHideTimer = useRef<number | null>(null);

  // Only change sizing for landscape videos
  const [isLandscape, setIsLandscape] = useState<boolean | null>(null);

  const type = useMemo(() => inferMime(videoUrl), [videoUrl]);

  // Derived: only show native controls when hovered OR playing
const controlsActive = showControls && isHover;

const onOverlayClick = () => {
  const v = videoRef.current;
  if (!v) return;

  if (v.paused) {
    v.play().catch(() => {});
    setUserPaused(false);
  } else {
    v.pause();
    setUserPaused(true);
  }
};

const musicLabel = [musicTitle, musicArtist]
  .filter(Boolean)
  .join(" — ");

  
  // Preload
  const effectivePreload = isVisible
    ? (preload ?? 'auto')
    : (preload ?? 'metadata');
  // ——— Visibility observer (preload ahead) ———
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { root: null, rootMargin: '800px 0px', threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, []);

  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  if (isVisible && !userPaused) {
    v.play().catch(() => {});
  } else {
    v.pause();
  }
}, [isVisible, userPaused, muted]);

  // ——— Pause on tab hidden; resume only if not user-paused ———
  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        v.pause();
      } else if (isVisible && !userPaused) {
        v.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isVisible, userPaused]);



  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = !!muted;
    wasMutedRef.current = !!muted;
  }, [muted]);


  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  setUserPaused(false); // allow autoplay
  setIsLandscape(null);

  v.currentTime = 0;

  if (isVisible) {
    v.play().catch(() => {});
  }
}, [videoUrl, isVisible]);


  // ——— Hover/touch handling for controls visibility ———
  const scheduleHide = (ms = 1200) => {
    if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
    hoverHideTimer.current = window.setTimeout(() => setIsHover(false), ms);
  };
  useEffect(() => {
    return () => {
      if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
    };
  }, []);

const containerHeight =
  isLandscape === true
    ? '50vh'
    : '90vh';


  // Style: only adjust for landscape; otherwise use your original sizing
  const videoStyle: CSSProperties =
    isLandscape
      ? {
          // Landscape → shrink-to-fit width, keep inside container
          width: '100%',
          height: 'auto',
          maxHeight: '100%',
          maxWidth: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
        }
      : {
          // Portrait/Square/Unknown → original behavior
          height: '100%',
          width: 'auto',
          objectFit: 'contain',
          backgroundColor: '#000',
        };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        height: containerHeight,
        marginTop: isLandscape ? '90%' : 0,
        maxHeight: '100vh',
        width: 'auto',
        maxWidth: '95vw',
        borderRadius: 12,
        backgroundColor: '#000',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
      onMouseEnter={() => {
        if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
        setIsHover(true);
      }}
      onMouseLeave={() => {
        // Hide controls after a short delay if paused; hide immediately if playing
        if (userPaused) scheduleHide(400);
        else setIsHover(false);
      }}
      onTouchStart={() => {
        // Mobile: brief reveal of controls
        setIsHover(true);
        scheduleHide(2000);
      }}
    >
      <video
        key={videoUrl}
        ref={videoRef}
        poster={poster}
        muted={muted}
        playsInline
        disablePictureInPicture
        controls={controlsActive}
        preload="metadata"
        controlsList="nodownload"
        style={videoStyle}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget as HTMLVideoElement;
          const w = v.videoWidth || 0;
          const h = v.videoHeight || 0;
          if (w && h) setIsLandscape(w > h);
        }}
        onLoadedData={(e) => {
          // Fallback in case metadata fires before dimensions are available in some browsers
          if (isLandscape === null) {
            const v = e.currentTarget as HTMLVideoElement;
            const w = v.videoWidth || 0;
            const h = v.videoHeight || 0;
            if (w && h) setIsLandscape(w > h);
          }
        }}
        onPlay={() => setUserPaused(false)}
        onPause={() => setUserPaused(true)}
        onVolumeChange={() => {
          const v = videoRef.current;
          if (!v) return;
          if (v.muted !== wasMutedRef.current) {
            wasMutedRef.current = v.muted;
            onMutedChange?.(v.muted); // ← notify parent
          }
        }}
        onError={(e) => {
          console.warn('Video failed to play:', videoUrl, e);
        }}
      >
        <source src={videoUrl} {...(type ? { type } : {})} />
        {t("smartVideo.html5Fallback")}
      </video>

{musicLabel && (
  <div
    style={{
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: isLandscape ? '60%' : '-1%',
      padding: '8px 12px 14px',
      fontSize: 11,
      color: '#7d7474',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      background:
        'linear-gradient(180deg, rgba(0,0,0,0.3) 10%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.75) 50%)',
      zIndex: 4,
      pointerEvents: 'auto',
    }}
  >
    <span style={{ flex: '0 0 auto' }}>🎵</span>

    {musicId ? (
      <a
        href={`/musicdetails/${musicId}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          minWidth: 0,
          color: '#797070',
          fontWeight: 500,
          fontSize: 11,
          textDecoration: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {musicLabel}
      </a>
    ) : (
      <span
        style={{
          minWidth: 0,
          color: '#767070',
          fontWeight: 500,
          fontSize: 11,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {musicLabel}
      </span>
    )}
  </div>
)}



      {/* Click overlay: only when we want click-to-toggle and native controls are NOT visible */}
      {/* Play overlay when paused */}
{clickToToggle && (
  <div
    onClick={onOverlayClick}
    style={{
      position: 'absolute',
      inset: 0,
      background: 'transparent',
      cursor: 'pointer',
      touchAction: 'pan-x',
    }}
  />
)}


    </div>
  );
};

export default memo(SmartVideoPlayerGallery);
